import { useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import {
  SchedulerTabsProps,
  SchedulerResourceAndMaterialConstraintsRowType,
  SchedulerResourceCapacityProfileRowType,
} from './Scheduler.types'
import {
  useScheduleProductionRequirements,
  useSchedulerTableHandlers,
} from './Scheduler.data'
import { SchedulerResourceCapacityProfileTable } from './SchedulerResourceCapacityProfile.muiTable'
import { WeekSelector } from './WeekSelector.view'

/**
 * Per-hour capacity for every resource constraint, one week at a time.
 *
 * The grid deliberately mirrors the Constraints tab of the schedule output
 * view (`SchedulerView.ConstraintsView`): resources down, hours across, day
 * headers, identical column widths. That tab shows what a run *used* against a
 * single flat limit; this one is where the limit itself stops being flat, so
 * reading the two together should not require translating between two shapes.
 *
 * Rows are the resource list, not this table's own contents — see
 * `rows` below.
 */
export const SchedulerResourceCapacityProfileView = ({
  schedulerId,
  iteration,
}: SchedulerTabsProps) => {
  const API = 'resource-capacity-profile'
  const CONSTRAINTS_API = 'resource-and-material-constraints'
  const [selectedWeek, setSelectedWeek] = useState(1)

  const {
    data: profileResponse = { data: [], predicate: undefined },
    isLoading,
  } = useScheduleProductionRequirements(
    schedulerId,
    API,
    iteration,
    selectedWeek
  )
  const { data: profileRows = [], predicate } = profileResponse

  // The resource list and each resource's flat amount live on the constraints
  // table, which is version-root — hence no week argument here, unlike the
  // profile fetch above.
  const {
    data: constraintsResponse = { data: [] },
    isLoading: isLoadingConstraints,
  } = useScheduleProductionRequirements(schedulerId, CONSTRAINTS_API, iteration)
  const constraints: SchedulerResourceAndMaterialConstraintsRowType[] =
    constraintsResponse.data ?? []

  const { flatAmounts, constraintTypes } = useMemo(() => {
    const flat: Record<string, number> = {}
    const types: Record<string, string> = {}
    constraints.forEach((c) => {
      const name = String(c?.ResourceConstraint ?? '')
      if (!name) return
      const amount = Number(c.ConstraintAmount)
      if (Number.isFinite(amount)) flat[name] = amount
      if (c.ConstraintType) types[name] = String(c.ConstraintType)
    })
    return { flatAmounts: flat, constraintTypes: types }
  }, [constraints])

  /**
   * One row per constrained resource, whether or not this week has a profile
   * for it — seeded from the constraint list exactly as the output grid's
   * `generateConstraintRows` seeds its rows, and for the same reason: a
   * resource with no row is a resource a planner cannot author, and its
   * absence would look identical to a resource that has no constraint at all.
   *
   * Saved profile rows are overlaid on top. A saved row whose resource has
   * since been removed from the constraints table is still shown, appended
   * after the seeded ones — dropping it would hide data the planner typed.
   *
   * `id` is re-issued here rather than taken from the service: the row set is
   * a union of two reads, so the service's per-read ids collide. It is only a
   * grid key — the save merges on `ResourceConstraint` — and the service drops
   * it on write.
   */
  const rows: SchedulerResourceCapacityProfileRowType[] = useMemo(() => {
    const saved = new Map<string, Record<string, unknown>>()
    ;(profileRows as Record<string, unknown>[]).forEach((r) => {
      const name = String(r?.ResourceConstraint ?? '')
      if (name) saved.set(name, r)
    })

    const ordered: string[] = []
    const seen = new Set<string>()
    constraints.forEach((c) => {
      const name = String(c?.ResourceConstraint ?? '')
      if (name && !seen.has(name)) {
        seen.add(name)
        ordered.push(name)
      }
    })
    saved.forEach((_row, name) => {
      if (!seen.has(name)) {
        seen.add(name)
        ordered.push(name)
      }
    })

    return ordered.map((name, index) => ({
      ...(saved.get(name) ?? {}),
      ResourceConstraint: name,
      id: index + 1,
    })) as SchedulerResourceCapacityProfileRowType[]
  }, [profileRows, constraints])

  const {
    updateTableData,
    uploadScheduleData,
    isSaving,
    isUploading,
    updateRowsStatus,
  } = useSchedulerTableHandlers<SchedulerResourceCapacityProfileRowType>(
    schedulerId,
    API,
    iteration,
    selectedWeek
  )

  return (
    <Box sx={{ height: 'calc(100vh - 240px)', minHeight: 400 }}>
      <WeekSelector value={selectedWeek} onChange={setSelectedWeek} />
      <Typography
        sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1, mt: -0.5 }}
      >
        A blank hour inherits the flat Constraint Amount shown on the left. Type
        a number to override that hour, or 0 to make the resource unavailable —
        the two are not the same. Clear a cell to go back to inheriting.
      </Typography>
      <SchedulerResourceCapacityProfileTable
        data={rows}
        isLoading={isLoading || isLoadingConstraints}
        updateTableData={updateTableData}
        uploadScheduleData={uploadScheduleData}
        isUploading={isUploading}
        isSaving={isSaving}
        updateRowsStatus={updateRowsStatus}
        predicate={predicate}
        flatAmounts={flatAmounts}
        constraintTypes={constraintTypes}
      />
    </Box>
  )
}
